import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Mail, Sparkles, Shield } from 'lucide-react';
import { supabase, getRedirectURL } from '@/lib/supabase';
import { extractErrorMessage } from '../../utils/errorUtils';
import { captureLoginFailed } from '../../utils/authProductEvents';
import { captureSupabaseAuthFailure } from '../../utils/clientOperationAnalytics';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { buildMcpAuthorizeUrlWithHandoff } from '@/services/mcpOAuthHandoff';

interface LoginSignupProps {
  onSuccess?: () => void;
}

export default function LoginSignup({}: LoginSignupProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [oauthFlow, setOauthFlow] = useState<any>(null);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const posthog = usePostHog();

  // Preserve deep links (dashboard tabs, Chrome Web Store extension setup) across auth redirects
  useEffect(() => {
    try {
      const path = `${location.pathname}${location.search}`;
      if (location.pathname === '/dashboard' || path.includes('setup=chrome-extension')) {
        sessionStorage.setItem('memara_post_login_redirect', path);
      }
    } catch (_) {}
  }, [location.pathname, location.search]);

  // Check if this is an OAuth flow from MCP server
  useEffect(() => {
    const oauthFlowParam = searchParams.get('oauth_flow');
    console.log('LoginSignup: Checking for OAuth flow param:', oauthFlowParam);
    console.log('LoginSignup: All search params:', Object.fromEntries(searchParams));
    
    // First check sessionStorage for persistent OAuth data (survives re-renders)
    const sessionOAuthData = sessionStorage.getItem('mcp_oauth_flow_active');
    if (sessionOAuthData) {
      try {
        const parsedOAuthData = JSON.parse(sessionOAuthData);
        console.log('LoginSignup: Restored OAuth flow from sessionStorage:', parsedOAuthData);
        setOauthFlow(parsedOAuthData);
        return; // Use sessionStorage data, don't re-parse URL
      } catch (err) {
        console.error('LoginSignup: Invalid OAuth data in sessionStorage, clearing:', err);
        sessionStorage.removeItem('mcp_oauth_flow_active');
      }
    }
    
    if (oauthFlowParam === 'mcp') {
      const oauthData = {
        client_id: searchParams.get('client_id'),
        redirect_uri: searchParams.get('redirect_uri'),
        response_type: searchParams.get('response_type'),
        scope: searchParams.get('scope'),
        state: searchParams.get('state'),
        code_challenge: searchParams.get('code_challenge'),
        code_challenge_method: searchParams.get('code_challenge_method'),
      };
      
      // Validate that we have the essential OAuth parameters
      const hasValidOAuthParams = oauthData.client_id && 
        oauthData.redirect_uri && 
        oauthData.response_type;
      
      if (hasValidOAuthParams) {
        console.log('Valid OAuth flow detected with params:', oauthData);
        setOauthFlow(oauthData);
        // Store in sessionStorage to persist across re-renders
        sessionStorage.setItem('mcp_oauth_flow_active', JSON.stringify(oauthData));
        console.log('LoginSignup: Stored OAuth flow in sessionStorage for persistence');
      } else {
        console.log('Invalid OAuth parameters detected, ignoring OAuth flow:', oauthData);
        // Clear any stale OAuth data
        localStorage.removeItem('mcp_oauth_flow');
        sessionStorage.removeItem('mcp_oauth_flow_active');
      }
    } else {
      // Not an OAuth flow - ensure storage is clean
      const existingOAuthData = localStorage.getItem('mcp_oauth_flow');
      if (existingOAuthData) {
        console.log('Clearing stale OAuth data from localStorage for regular login');
        localStorage.removeItem('mcp_oauth_flow');
      }
      // Also clear sessionStorage if no OAuth flow
      sessionStorage.removeItem('mcp_oauth_flow_active');
    }
  }, [searchParams]);

  // Handle already authenticated users with OAuth flow
  useEffect(() => {
    const handleAuthenticatedOAuthFlow = async () => {
      console.log('LoginSignup: handleAuthenticatedOAuthFlow called with:', {
        isAuthenticated,
        isLoading,
        oauthFlow: !!oauthFlow,
        oauthFlowData: oauthFlow
      });

      // Only proceed if user is authenticated, not loading, and we have OAuth flow
      if (!isAuthenticated || isLoading || !oauthFlow) {
        console.log('LoginSignup: Skipping OAuth redirect due to conditions:', {
          authenticated: isAuthenticated,
          loading: isLoading,
          hasOauthFlow: !!oauthFlow
        });
        return;
      }

      console.log('LoginSignup: User already authenticated with OAuth flow - redirecting to MCP server');

      try {
        // Get the current session token to pass to MCP server
        const { data: sessionData } = await supabase.auth.getSession();
        console.log('LoginSignup: Retrieved session data:', {
          hasSession: !!sessionData.session,
          hasAccessToken: !!sessionData.session?.access_token
        });
        
        const session = sessionData.session;
        if (session?.access_token && session.refresh_token) {
          const oauthParams: Record<string, string> = {};
          
          if (oauthFlow.client_id) oauthParams.client_id = oauthFlow.client_id;
          if (oauthFlow.redirect_uri) oauthParams.redirect_uri = oauthFlow.redirect_uri;
          if (oauthFlow.response_type) oauthParams.response_type = oauthFlow.response_type;
          if (oauthFlow.scope) oauthParams.scope = oauthFlow.scope;
          if (oauthFlow.state) oauthParams.state = oauthFlow.state;
          if (oauthFlow.code_challenge) oauthParams.code_challenge = oauthFlow.code_challenge;
          if (oauthFlow.code_challenge_method) oauthParams.code_challenge_method = oauthFlow.code_challenge_method;

          const mcpOAuthUrl = await buildMcpAuthorizeUrlWithHandoff(
            session.access_token,
            session.refresh_token,
            oauthParams
          );
          console.log('LoginSignup: Redirecting authenticated user to MCP OAuth URL');
          
          sessionStorage.removeItem('mcp_oauth_flow_active');
          window.location.href = mcpOAuthUrl;
        } else {
          console.log('LoginSignup: No session tokens found, cannot redirect to MCP server');
        }
      } catch (error) {
        console.error('LoginSignup: Error handling authenticated OAuth flow:', error);
        setError(t('authenticationError', { ns: 'auth' }));
      }
    };

    handleAuthenticatedOAuthFlow();
  }, [isAuthenticated, isLoading, oauthFlow, t]);

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Build redirect URL with OAuth parameters if this is an OAuth flow
      let redirectTo = `${getRedirectURL()}/auth/callback`;
      
      if (oauthFlow) {
        // Include OAuth parameters in the redirect URL so they survive the email click
        const oauthQuery = new URLSearchParams(oauthFlow).toString();
        redirectTo = `${getRedirectURL()}/auth/callback?oauth_flow=mcp&${oauthQuery}`;
        console.log('Magic Link: Including OAuth params in redirect URL:', redirectTo);
      }

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectTo,
        },
      });

      if (error) {
        captureLoginFailed(posthog, error);
        captureSupabaseAuthFailure(posthog, {
          surface: 'login_signup',
          operation: 'magic_link',
          error,
        });
        setError(extractErrorMessage(error, t('failedToSendMagicLink', { ns: 'auth' })));
      } else {
        setMagicLinkSent(true);
        
        // If this is an OAuth flow, store the parameters for after authentication
        if (oauthFlow) {
          // Validate OAuth data before storing
          const hasValidOAuthParams = oauthFlow.client_id && 
            oauthFlow.redirect_uri && 
            oauthFlow.response_type;
          
          if (hasValidOAuthParams) {
            localStorage.setItem('mcp_oauth_flow', JSON.stringify(oauthFlow));
            console.log('Magic Link: Stored valid OAuth flow parameters for callback:', oauthFlow);
            console.log('Magic Link: localStorage now contains:', localStorage.getItem('mcp_oauth_flow'));
          } else {
            console.log('Magic Link: Skipping localStorage storage - invalid OAuth parameters:', oauthFlow);
          }
        }
      }
    } catch (err) {
      captureLoginFailed(posthog, err);
      captureSupabaseAuthFailure(posthog, {
        surface: 'login_signup',
        operation: 'magic_link_exception',
        error: err,
      });
      setError(extractErrorMessage(err, t('failedToSendMagicLink', { ns: 'auth' })));
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google') => {
    setLoading(true);
    setError('');

    try {
      // If this is an OAuth flow, store the parameters for after authentication
      if (oauthFlow) {
        // Validate OAuth data before storing
        const hasValidOAuthParams = oauthFlow.client_id && 
          oauthFlow.redirect_uri && 
          oauthFlow.response_type;
        
        if (hasValidOAuthParams) {
          localStorage.setItem('mcp_oauth_flow', JSON.stringify(oauthFlow));
          console.log('Social Login: Stored valid OAuth flow parameters for callback:', oauthFlow);
          console.log('Social Login: localStorage now contains:', localStorage.getItem('mcp_oauth_flow'));
        } else {
          console.log('Social Login: Skipping localStorage storage - invalid OAuth parameters:', oauthFlow);
        }
      }

      // Build redirect URL with OAuth parameters if this is an OAuth flow
      let redirectTo = `${getRedirectURL()}/auth/callback`;
      
      if (oauthFlow) {
        // Include OAuth parameters in the redirect URL so they survive the OAuth flow
        const oauthQuery = new URLSearchParams(oauthFlow).toString();
        redirectTo = `${getRedirectURL()}/auth/callback?oauth_flow=mcp&${oauthQuery}`;
        console.log('Social Login: Including OAuth params in redirect URL:', redirectTo);
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: redirectTo,
        },
      });

      if (error) {
        captureLoginFailed(posthog, error);
        captureSupabaseAuthFailure(posthog, {
          surface: 'login_signup',
          operation: 'oauth_google',
          error,
        });
        setError(extractErrorMessage(error, t('socialLoginFailed', { ns: 'auth' })));
      }
    } catch (err) {
      captureLoginFailed(posthog, err);
      captureSupabaseAuthFailure(posthog, {
        surface: 'login_signup',
        operation: 'oauth_google_exception',
        error: err,
      });
      setError(extractErrorMessage(err, t('socialLoginFailed', { ns: 'auth' })));
    } finally {
      setLoading(false);
    }
  };



  // Show magic link sent confirmation
  if (magicLinkSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
            <CardHeader className="text-center pb-2">
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="mx-auto w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4"
              >
                <Mail className="w-8 h-8 text-white" />
              </motion.div>
              
              <CardTitle className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                {t('authenticationLinkSent', { ns: 'auth' })}
              </CardTitle>
              <CardDescription className="text-gray-600 mt-2">
                {t('secureAccessLinkSent', { email, ns: 'auth' })}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="text-center space-y-4">
                <p className="text-sm text-gray-600">
                  {t('clickLinkInEmail', { ns: 'auth' })}
                </p>
                
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 text-indigo-700">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-medium">{t('magicLinkSent', { ns: 'auth' })}</span>
                  </div>
                  <p className="text-xs text-indigo-600 mt-1">
                    {t('linkExpiresInOneHour', { ns: 'auth' })}
                  </p>
                </div>

                <Button
                  onClick={() => {
                    setMagicLinkSent(false);
                    setEmail('');
                  }}
                  variant="outline"
                  className="w-full"
                >
                  {t('sendAnotherLink', { ns: 'auth' })}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-2">
            {oauthFlow && (
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="mx-auto w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4"
              >
                <Shield className="w-8 h-8 text-white" />
              </motion.div>
            )}
            
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              {oauthFlow ? 'Authorize Claude Desktop' : t('accessYourAccount', { ns: 'auth' })}
            </CardTitle>
            <CardDescription className="text-gray-600">
              {oauthFlow ? 'Sign in to authorize Claude Desktop integration' : t('secureAuthentication', { ns: 'auth' })}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Social Login Button */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 border-gray-200 hover:bg-gray-50 text-gray-700"
              onClick={() => handleSocialLogin('google')}
              disabled={loading}
            >
              <Mail className="w-4 h-4 mr-2" />
              {t('continueWithGoogle', { ns: 'auth' })}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">{t('orContinueWithEmail', { ns: 'auth' })}</span>
              </div>
            </div>

            {/* Magic Link Form */}
            <form onSubmit={handleMagicLinkSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('emailAddress', { ns: 'auth' })}</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder={t('enterYourEmail', { ns: 'auth' })}
                  className="w-full"
                />
              </div>

              {error && (
                <div data-testid="error-message" className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                data-testid="magic-link-btn"
                disabled={loading || !email.trim()}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
              >
                {loading ? t('sending', { ns: 'auth' }) : t('sendMagicLink', { ns: 'auth' })}
              </Button>
            </form>

            <div className="text-center">
              <p className="text-xs text-gray-500">
                {t('byContinuing', { ns: 'auth' })}{' '}
                <a href="/legal/terms" className="text-indigo-600 hover:underline">
                  {t('termsOfService', { ns: 'auth' })}
                </a>{' '}
                {t('and', { ns: 'auth' })}{' '}
                <a href="/legal/privacy-policy" className="text-indigo-600 hover:underline">
                  {t('privacyPolicy', { ns: 'auth' })}
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
} 